/**
 * Admin: Demo Tools — one-click seed of test users for every role + cleanup.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FlaskConical, Trash2, Copy, UserCog, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  // Iter-192b · CEO rename — the top-of-house PM tier is labeled
  // "Owner & Manager" so demo copy matches the pm_sub_role gating
  // used across the PM dashboard (Owner + Manager + Assistant Manager
  // are the only sub-roles that see Site Staff Users + Billing/A/R).
  property_manager: 'Owner & Manager',
  manager: 'Regional Manager',
  supervisor: 'Supervisor',
  crew_leader: 'Crew Leader',
  agent: 'Site Agent',
  tenant: 'Resident / Tenant',
};

// Feb 2026 iter-78 pt 9 — Cleaner accounts are still stored with role=agent
// but flagged via primary_service="cleaning". Demo Tools surfaces them as a
// distinct card by looking at the `service` field returned by the seed
// endpoint (see routes/operations.py demo-seed).
const serviceLabel = (acct) => (acct.service === 'cleaning' ? 'Cleaner' : null);

export default function AdminDemoSeed() {
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState(null);
  const [demoProp, setDemoProp] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter-103 · Item 5 — Role selector. Ops user must pick Valet OR
  // Cleaners; the corresponding section renders exclusively. The two
  // sections never render together, never share data.
  const [role, setRole] = useState('valet'); // Iter-178 · Phase 9 — defaults to 'valet' after selector removal

  const seed = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/admin/demo-seed`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed');
      } else {
        setAccounts(data.accounts || []);
        setGroups(data.groups || null);
        setDemoProp(data.demo_property);
        toast.success('Demo accounts ready — copy the credentials below');
      }
    } finally { setLoading(false); }
  };

  const wipe = async () => {
    if (!window.confirm('Remove all demo accounts and the Demo Trust Tower property?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/admin/demo-seed`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Failed');
      else {
        toast.success(`Removed ${data.users_removed} demo users + ${data.properties_removed} property`);
        setAccounts([]);
        setGroups(null);
        setDemoProp(null);
      }
    } finally { setLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied');
  };

  const impersonate = async (account) => {
    if (!window.confirm(`Impersonate ${account.name} (${account.email})?\n\nYour current admin session will be paused; you can return any time from the gold banner that appears at the top of the screen.`)) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/admin/demo-seed/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: account.email }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not impersonate');
        return;
      }
      toast.success(`Switched to ${account.name}`);
      // Route directly to the impersonated role's dashboard so the admin
      // sees exactly what that user sees, without leaving the session.
      // Iter-78: manager (Regional Manager) now uses StaffDashboard alongside
      // supervisor / crew_leader / agent — no more driver dashboard fallback.
      const role = data.user?.role;
      const dest =
        role === 'property_manager'  ? '/property-manager-dashboard' :
        role === 'driver'            ? '/driver-dashboard' :
        ['manager', 'supervisor', 'crew_leader', 'agent', 'site_agent', 'staff'].includes(role)
                                     ? '/staff-dashboard' :
        ['admin', 'company_manager'].includes(role)
                                     ? '/company-manager-dashboard' :
        role === 'tenant'            ? '/tenant-dashboard' :
                                       '/';
      // Hard reload so AuthContext refetches /api/auth/me with the new cookie.
      window.location.href = dest;
    } finally { setLoading(false); }
  };

  const sendDigestNow = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/admin/send-bonus-digest-now`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not send digest');
        return;
      }
      toast.success(
        `Digest fired · ${data.sent}/${data.leaders_targeted} crew leader email${data.sent === 1 ? '' : 's'} queued`,
      );
    } finally { setLoading(false); }
  };

  return (
    <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6" data-testid="admin-demo-seed-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <FlaskConical className="h-6 w-6 text-[#FFD60A]" />
            DEMO TOOLS — TEST EVERY ROLE
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Generate working demo logins for every role + a sandbox property. Use them to walk through every flow without polluting real data.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={seed}
            disabled={loading}
            className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
            data-testid="admin-demo-seed-btn"
          >
            <FlaskConical className="h-4 w-4 mr-2" /> Generate Demo Badges
          </Button>
          <Button
            onClick={wipe}
            disabled={loading || accounts.length === 0}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/10"
            data-testid="admin-demo-wipe-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Wipe Demo Data
          </Button>
          {/* Iter-78 pt 11 — CEO override: fire the Friday Bonus Digest
              email immediately for testing / ad-hoc reporting. */}
          <Button
            onClick={sendDigestNow}
            disabled={loading}
            variant="outline"
            className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
            data-testid="admin-send-bonus-digest-btn"
          >
            <Send className="h-4 w-4 mr-2" /> Send Bonus Digest Now
          </Button>
        </div>
      </div>

      {demoProp && (
        <div className="mt-2 mb-4 text-xs text-zinc-400">
          Demo property: <span className="text-[#FFD60A] font-mono">{demoProp.property_name}</span>
          <span className="text-zinc-600"> · {demoProp.property_id}</span>
        </div>
      )}

      {accounts.length > 0 && (
        <>
          {/* Iter-103 · Item 5 — Ops user must pick a service line role
              before the badges render. Valet and Cleaners never appear
              together. Shared roster (PM · Tenant) is shown alongside
              the chosen role's board. */}
          {/* Iter-178 · Phase 9 — Demo Tools badge selector removed.
              The persistent AdminControlStrip at the top of the admin
              dashboard now drives Role / Country / State / Region /
              City / Period / Presets. Role defaults to 'valet' so the
              existing valet roster still renders below without a
              second selector. */}

          {role === 'valet' && (
            <DemoRoster
              title="Valet HQ Demo Badges"
              testId="admin-demo-board-valet"
              accent="#FFD60A"
              note="Valet field staff hierarchy · NE-DEMO prefix"
              rows={groups?.valet || accounts.filter((a) => a.service === 'valet')}
              impersonate={impersonate}
              copy={copy}
              loading={loading}
            />
          )}

          {role === 'cleaners' && (
            <>
              <DemoRoster
                title="Cleaners HQ Demo Badges"
                testId="admin-demo-board-cleaners"
                accent="#22d3ee"
                note="Cleaner field staff hierarchy · role-encoded CLN prefix (CLN-RM / CLN-SUP / CLN-CL / CLN-SA)"
                rows={groups?.cleaners || accounts.filter((a) => a.service === 'cleaning')}
                impersonate={impersonate}
                copy={copy}
                loading={loading}
              />
              <div className="h-4" />
              <CleanerCommercialTypeBoard copy={copy} />
              {/* Iter-180 · Phase 5 — 6 commercial tenant demos (Office
                  / Mall / Retail × A/H + A/U). Only surfaces when the
                  admin is viewing the Cleaners section. */}
              {accounts.some((a) => a.context === 'cleaners') && (
                <>
                  <div className="h-4" />
                  <DemoRoster
                    title="Commercial Tenant Demos (Cleaners scope)"
                    testId="admin-demo-board-cleaners-tenants"
                    accent="#f59e0b"
                    note="Office / Mall / Retail — Account Holder (A/H) + Authorizer User (A/U) per property type"
                    rows={accounts.filter((a) => a.context === 'cleaners')}
                    impersonate={impersonate}
                    copy={copy}
                    loading={loading}
                  />
                </>
              )}
            </>
          )}

          {role && (groups?.shared?.length || accounts.some((a) => !a.service)) && (
            <>
              <div className="h-4" />
              <DemoRoster
                title="Shared Demo Accounts"
                testId="admin-demo-board-shared"
                accent="#a3a3a3"
                note="Property Manager + Tenant used by both service lines"
                rows={groups?.shared || accounts.filter((a) => !a.service && a.context !== 'cleaners')}
                impersonate={impersonate}
                copy={copy}
                loading={loading}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
}

function DemoRoster({ title, testId, accent, note, rows, impersonate, copy, loading }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      className="rounded-lg p-4"
      style={{ backgroundColor: '#0f0f0f', border: `2px solid ${accent}` }}
      data-testid={testId}
    >
      <div className="mb-3">
        <h3
          className="text-lg font-black uppercase tracking-widest flex items-center gap-2"
          style={{ color: accent, fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid={`${testId}-heading`}
        >
          {title}
        </h3>
        <p className="text-xs text-zinc-400 mt-1">{note}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((a) => (
          <div
            key={a.email}
            className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 flex flex-col"
            data-testid={`${testId}-row-${a.role}${a.sub_role ? `-${a.sub_role.toLowerCase().replace('/','-')}` : ''}`}
          >
            <div className="flex items-center justify-between mb-1">
              <Badge className="bg-[#FFD60A] text-black" data-testid={`${testId}-role-${a.role}`}>
                {a.label || ROLE_LABELS[a.role] || a.role}
              </Badge>
              {a.badge && (
                <span className="text-xs font-mono text-zinc-300" data-testid={`${testId}-badge-${a.badge}`}>
                  {a.badge}
                </span>
              )}
              {a.unit && <span className="text-xs font-mono text-zinc-300">Unit {a.unit}</span>}
            </div>
            <div className="text-sm text-white font-semibold">{a.name}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-zinc-300 font-mono break-all">{a.email}</span>
              <button onClick={() => copy(a.email)} className="text-zinc-400 hover:text-[#FFD60A]" title="Copy email">
                <Copy className="h-3 w-3" />
              </button>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-zinc-400">Password:</span>
              <span className="text-xs font-mono text-emerald-400">{a.password}</span>
              <button onClick={() => copy(a.password)} className="text-zinc-400 hover:text-[#FFD60A]" title="Copy password">
                <Copy className="h-3 w-3" />
              </button>
            </div>
            <Button
              size="sm"
              onClick={() => impersonate(a)}
              disabled={loading}
              className="mt-3 bg-[#C9A962] hover:bg-[#b89651] text-black font-bold w-full"
              data-testid={`${testId}-impersonate-${a.role}`}
            >
              <UserCog className="h-3.5 w-3.5 mr-2" /> Impersonate this account
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * CleanerCommercialTypeBoard — Iter-103 · Item 5.
 *
 * Nine commercial business-type sections inside the Cleaners HQ Demo
 * Badges area. Each section shows the NECS service line + tier
 * (Basic/Standard/Premium) previews, and a "Generate" button that
 * on-demand provisions a demo badge for that specific business type
 * via POST /api/live-demo/launch. No pre-seeding into db.users.
 */
// Iter-149 · Section 43 — commercial-type icon map (expanded from 9 → 12).
// LINE_ICON used inside CommercialCleanersBoard). Kept local here so
// the Demo Tools tile shows the same emblem the field team sees.
const CLEANER_TYPE_ICON = {
  office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦', church: '⛪',
  bank: '🏦', stadium: '🏟️', airport: '✈️',
};

// Iter-149 · Section 9/33 — Strategic Enterprise verticals need extra
// review before final pricing. This set drives the Section 43.D internal
// note that appears on the impersonation card.
const STRATEGIC_ENTERPRISE_TYPES = new Set(['bank', 'stadium', 'airport']);

function CleanerCommercialTypeBoard({ copy }) {
  const [lines, setLines] = useState([]);
  const [generated, setGenerated] = useState({});
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [impersonating, setImpersonating] = useState(null);
  const [demoAccountsReady, setDemoAccountsReady] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/commercial/service-lines?visibility=commercial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.service_lines) setLines(d.service_lines); });
  }, []);

  const generate = async (line) => {
    setBusy(line.property_type);
    try {
      const res = await fetch(`${API}/api/live-demo/launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_type: line.property_type,
          submitted: { property_name: `Demo · ${line.necs_name}` },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setGenerated((g) => ({ ...g, [line.property_type]: data }));
      }
    } finally {
      setBusy(null);
    }
  };

  const impersonate = async (line) => {
    if (!window.confirm(
      `Impersonate the ${line.necs_name} commercial client?\n\n`
      + 'You will land on their Property Manager dashboard. Return any '
      + 'time from the gold banner that appears at the top of the screen.',
    )) return;
    setImpersonating(line.property_type);
    try {
      if (!demoAccountsReady) {
        try {
          const g = await fetch(`${API}/api/cleaners/demo-accounts/generate`, {
            method: 'POST', credentials: 'include',
          });
          if (g.ok) setDemoAccountsReady(true);
        } catch { /* fall through — impersonate call will surface a 404 */ }
      }
      const email = `demo_cleaners_${line.property_type}@necsdemo.com`;
      const res = await fetch(`${API}/api/ops/admin/demo-seed/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not impersonate this commercial client.');
        return;
      }
      toast.success(`Switched to ${line.necs_name} demo client`);
      window.location.href = '/property-manager-dashboard';
    } finally {
      setImpersonating(null);
    }
  };

  // Iter-149 · Section 43.A — split into Standard Commercial (1-9) vs
  // Strategic Enterprise (10-12) so the admin sees the 12-vertical
  // architecture at a glance.
  const stdLines = lines.filter((l) => !STRATEGIC_ENTERPRISE_TYPES.has(l.property_type));
  const strategicLines = lines.filter((l) => STRATEGIC_ENTERPRISE_TYPES.has(l.property_type));

  const renderCard = (line, index) => {
    const gen = generated[line.property_type];
    const isBusy = busy === line.property_type;
    const isExpanded = !!expanded[line.property_type];
    const isStrategic = STRATEGIC_ENTERPRISE_TYPES.has(line.property_type);
    return (
      <div
        key={line.property_type}
        className="rounded-md p-3"
        style={{
          backgroundColor: '#1a1a1a',
          border: `1px solid ${isStrategic ? '#f59e0b' : '#3f3f46'}`,
        }}
        data-testid={`admin-demo-cleaner-type-${line.property_type}`}
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: '#22d3ee' }}>
            <span aria-hidden="true" data-testid={`admin-demo-cleaner-icon-${line.property_type}`}>
              {CLEANER_TYPE_ICON[line.property_type] || '🧼'}
            </span>
            {line.necs_name}
            {/* Iter-149 · Section 43.C — DEMO badge on every card. */}
            <span
              className="ml-1 text-[8px] px-1.5 py-0.5 rounded font-black"
              style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid #fbbf24' }}
              data-testid={`admin-demo-cleaner-demo-badge-${line.property_type}`}
            >
              DEMO
            </span>
          </p>
          <button
            type="button"
            onClick={() => setExpanded((e) => ({ ...e, [line.property_type]: !e[line.property_type] }))}
            className="text-[10px] text-zinc-400 hover:text-white"
          >
            {isExpanded ? 'Hide' : 'Show'} packages
          </button>
        </div>
        <p className="text-sm text-white font-semibold">{line.property_label}</p>
        <p className="text-[11px] text-zinc-500 mt-1">{line.tagline}</p>

        {/* Iter-149 · Section 43.D — Strategic Enterprise internal note. */}
        {isStrategic && (
          <p
            className="text-[10px] mt-2 p-2 rounded"
            style={{ backgroundColor: 'rgba(245,158,11,0.10)', color: '#fcd34d', border: '1px dashed #f59e0b' }}
            data-testid={`admin-demo-strategic-note-${line.property_type}`}
          >
            <strong>Strategic Enterprise Note:</strong> Confirm security boundary language and require
            a completed Facility Assessment before quoting final pricing for this account type.
          </p>
        )}

        {isExpanded && (
          <div className="mt-3 grid grid-cols-3 gap-1" data-testid={`admin-demo-cleaner-tiers-${line.property_type}`}>
            {(line.tiers || []).map((t) => (
              <div
                key={t.key}
                className="rounded p-2 text-center"
                style={{ backgroundColor: '#0a0a0a', border: '1px solid #3f3f46' }}
              >
                <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: '#22d3ee' }}>{t.name}</p>
                <p className="text-[9px] text-zinc-500 mt-0.5">{t.frequency}</p>
                <p className="text-[9px] text-zinc-400 mt-1">{(t.included || []).length} features</p>
              </div>
            ))}
          </div>
        )}

        <Button
          size="sm"
          onClick={() => generate(line)}
          disabled={isBusy || !!gen}
          className="mt-3 w-full font-bold"
          style={{ backgroundColor: gen ? '#10b981' : '#22d3ee', color: '#0a0a0a' }}
          data-testid={`admin-demo-cleaner-generate-${line.property_type}`}
        >
          {isBusy ? 'Generating…' : gen ? '✓ Generated' : 'Generate Demo Badge'}
        </Button>

        <Button
          size="sm"
          onClick={() => impersonate(line)}
          disabled={impersonating === line.property_type}
          className="mt-2 w-full font-bold"
          style={{ backgroundColor: '#C9A962', color: '#0a0a0a' }}
          data-testid={`admin-demo-cleaner-impersonate-${line.property_type}`}
        >
          <UserCog className="h-3.5 w-3.5 mr-2" />
          {impersonating === line.property_type
            ? 'Switching…'
            : `Impersonate ${line.necs_name}`}
        </Button>

        {gen && (
          <div className="mt-2 text-[10px]" data-testid={`admin-demo-cleaner-preview-${line.property_type}`}>
            <p className="text-zinc-300">
              Sandbox: <span className="font-mono">{gen.demo_session_id}</span>
            </p>
            <p className="text-zinc-300">
              Tier: <span className="font-bold">{gen.tier}</span> · Service line: <span className="font-bold">{gen.service_line}</span>
            </p>
            {/* Iter-149 · Section 43.F — surface contract duration + payment
                method on any demo account with a simulated contract. */}
            <p className="text-zinc-400">
              Contract: <span className="font-bold text-white">12 months</span> · Payment: <span className="font-bold text-white">ACH</span>
            </p>
            <button
              type="button"
              onClick={() => copy(gen.demo_session_id)}
              className="text-[10px] text-zinc-400 hover:text-white underline"
            >
              Copy session id
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="rounded-lg p-4"
      style={{ backgroundColor: '#0f0f0f', border: '2px solid #22d3ee' }}
      data-testid="admin-demo-cleaner-commercial-types"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3
          className="text-lg font-black uppercase tracking-widest"
          style={{ color: '#22d3ee', fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          NECS Integrated Facility Care · Commercial Business Types (12)
        </h3>
        {/* Iter-149 · Section 43.E — Quote engine demo reset control. */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            try { window.dispatchEvent(new Event('necs:reset-quote-demo')); } catch {}
            try { sessionStorage.removeItem('necs_quote_demo_state'); } catch {}
            toast.success('Quote engine demo reset to Step 1');
          }}
          data-testid="admin-demo-cleaner-reset-quote"
          className="text-[10px] font-bold"
        >
          Reset Quote Demo → Step 1
        </Button>
      </div>
      <p className="text-xs text-zinc-400 mb-4">
        Twelve commercial verticals grouped by NECS account tier. Standard Commercial (1-9) covers
        core business types; Strategic Enterprise (10-12) covers Bank, Stadium and Airport accounts
        requiring facility assessment before final pricing.
      </p>

      {/* Iter-149 · Section 43.A — Standard Commercial group */}
      <div className="mb-6" data-testid="admin-demo-group-standard-commercial">
        <p className="text-[11px] uppercase tracking-[0.24em] font-black mb-2" style={{ color: '#22d3ee' }}>
          Standard Commercial · Verticals 1-9
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stdLines.map(renderCard)}
        </div>
      </div>

      {/* Iter-149 · Section 43.A — Strategic Enterprise group */}
      <div data-testid="admin-demo-group-strategic-enterprise">
        <p className="text-[11px] uppercase tracking-[0.24em] font-black mb-2" style={{ color: '#f59e0b' }}>
          Strategic Enterprise · Verticals 10-12
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {strategicLines.map(renderCard)}
        </div>
      </div>
    </div>
  );
}

